import NewDomainPage from 'pages/customer/NewDomainPage.vue'
import NewWebhostingPage from 'pages/customer/NewWebhostingPage.vue'
import SharingCenterRsvpPage from 'pages/customer/SharingCenterRsvpPage.vue'
import UnsubscribeMailPage from 'pages/customer/UnsubscribeMailPage.vue'
import { pinia } from 'src/modules/pinia.js'

const ResetPasswordPage = () => import('pages/auth/ResetPasswordPage.vue')
const VerifyPage = () => import('pages/auth/VerifyPage.vue')
const VerifyIdentityPage = () => import('pages/auth/VerifyIdentityPage.vue')
const ActivateMFAPage = () => import('pages/auth/ActivateMFAPage.vue')
const VerifyMFAPage = () => import('pages/auth/VerifyMFAPage.vue')
const ChooseMFAMethodPage = () => import('pages/auth/ChooseMFAMethodPage.vue')
const LoginPage = () => import('pages/auth/LoginPage.vue')
const ForgotPage = () => import('pages/auth/ForgotPage.vue')
const LogoutPage = () => import('pages/auth/LogoutPage.vue')
const DocsRedirectPage = () => import('pages/auth/DocsRedirectPage.vue')
const AccountInvitePage = () => import('pages/auth/AccountInvitePage.vue')

const AuthLayout = () => import('layouts/AuthLayout.vue')
const CustomerLayout = () => import('layouts/CustomerLayout.vue')
const CustomerProfilePage = () => import('pages/customer/CustomerProfilePage.vue')
const UserSettingsPage = () => import('pages/customer/UserSettingsPage.vue')
const ProfilePage = () => import('pages/ProfilePage.vue')
const UnauthorizedPage = () => import('pages/UnauthorizedPage.vue')
const MainView = () => import('pages/customer/MainPage.vue')
const WebhostingPage = () => import('pages/customer/WebhostingPage.vue')
const WebhostingDetailsPage = () => import('pages/customer/WebhostingDetailsPage.vue')
const CustomerUsersPage = () => import('pages/customer/CustomerUsersPage.vue')
const DomainsPage = () => import('pages/customer/DomainsPage.vue')
const DomainDetailsPage = () => import('pages/customer/DomainDetailsPage.vue')
const SharingCenterPage = () => import('pages/customer/SharingCenterPage.vue')
const StatisticsPage = () => import('pages/customer/StatisticsPage.vue')
const DocsPassthroughPage = () => import('pages/DocsPassthroughPage.vue')
const InvoicesPage = () => import('pages/customer/InvoicesPage.vue')
const LicensesPage = () => import('pages/customer/LicensesPage.vue')
const BackupsPage = () => import('pages/customer/BackupsPage.vue')
const ErrorNotFound = () => import('pages/ErrorNotFound.vue')

const requireBackupPermission = () => {
  const authStore = useAuthStore(pinia)
  return authStore.hasServicePermission('customer-assets', ['findBackups_assigned', 'findBackups_all']) ? true : { name: 'unauthorized' }
}

const routesCustomerPortal = [
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        path: 'activate-mfa',
        name: 'activateMfa',
        component: ActivateMFAPage,
        meta: {
          requiresAuth: true,
          requiresMFA: false,
        },
      },
      {
        path: 'verify-mfa',
        name: 'verifyMfa',
        component: VerifyMFAPage,
        meta: {
          requiresAuth: true,
          requiresMFA: false,
        },
      },
      {
        path: 'choose-mfa-method',
        name: 'chooseMfaMethod',
        component: ChooseMFAMethodPage,
        meta: {
          requiresAuth: true,
          requiresMFA: false,
        },
      },
      {
        path: 'verify-identity',
        name: 'verifyIdentity',
        component: VerifyIdentityPage,
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: 'verify',
        name: 'verify',
        component: VerifyPage,
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: 'account-invite/:id',
        name: 'account-invite',
        component: AccountInvitePage,
        meta: {
          requiresAuth: false,
          requiresMFA: false,
        },
      },
      {
        path: 'login',
        name: 'login',
        component: LoginPage,
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: 'logout',
        name: 'logout',
        component: LogoutPage,
        meta: {
          requiresAuth: false,
          requiresMFA: false,
        },
      },
      {
        path: 'docs-redirect',
        name: 'docsRedirect',
        component: DocsRedirectPage,
        meta: {
          requiresAuth: false,
          requiresMFA: false,
        },
      },
      {
        path: 'forgot',
        name: 'forgot',
        component: ForgotPage,
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: 'reset',
        name: 'reset',
        component: ResetPasswordPage,
        meta: {
          requiresAuth: false,
        },
      },
    ],
  },
  {
    path: '/',
    component: CustomerLayout,
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: 'settings',
        name: 'settings',
        component: UserSettingsPage,
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'profile',
        name: 'profile',
        component: ProfilePage,
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'customer-users',
        name: 'customer-users',
        component: CustomerUsersPage,
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'api-accounts',
        name: 'api-accounts',
        component: CustomerUsersPage,
        props: {
          userTypeMode: 'api',
        },
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'customer-profile',
        name: 'customer-profile',
        component: CustomerProfilePage,
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'unauthorized',
        name: 'unauthorized',
        component: UnauthorizedPage,
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: '',
        name: 'main',
        component: MainView,
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'invoices',
        name: 'invoices',
        component: InvoicesPage,
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '',
        component: LicensesPage,
        meta: { requiresAuth: true },
        children: [
          { path: 'licenses', name: 'licenses', redirect: { name: 'licenses-overview' } },
          {
            path: 'licenses/overview',
            name: 'licenses-overview',
            component: () => import('pages/customer/Licenses/CustomerLicensesOverviewPage.vue'),
            meta: { requiresAuth: true, label: 'Overview' },
          },
          {
            path: 'licenses/omni',
            name: 'licenses-omni',
            component: () => import('pages/customer/Licenses/CustomerOmniLicensesPage.vue'),
            meta: { requiresAuth: true, label: 'OMNI licenses' },
          },
          {
            path: 'licenses/microsoft',
            name: 'licenses-microsoft',
            component: () => import('pages/customer/Licenses/CustomerMicrosoftLicensesPage.vue'),
            meta: { requiresAuth: true, label: 'Microsoft licenses' },
          },
        ],
      },
      {
        path: '',
        component: BackupsPage,
        beforeEnter: requireBackupPermission,
        meta: { requiresAuth: true },
        children: [
          { path: 'backups', name: 'backups', redirect: { name: 'backups-overview' } },
          {
            path: 'backups/overview',
            name: 'backups-overview',
            component: () => import('pages/customer/backups/BackupsOverviewPage.vue'),
            meta: { requiresAuth: true, label: 'Overview' },
          },
          {
            path: 'backups/microsoft-365',
            name: 'backups-microsoft-365',
            component: () => import('pages/customer/backups/Microsoft365BackupsPage.vue'),
            meta: { requiresAuth: true, label: 'Microsoft 365' },
          },
          {
            path: 'backups/locked-vault',
            name: 'backups-locked-vault',
            component: () => import('pages/customer/backups/LockedVaultBackupsPage.vue'),
            meta: { requiresAuth: true, label: 'Locked Vault' },
          },
          {
            path: 'backups/cloud',
            name: 'backups-cloud',
            component: () => import('pages/customer/backups/CloudBackupsPage.vue'),
            meta: { requiresAuth: true, label: 'Cloud backups' },
          },
        ],
      },
      {
        path: 'webhosting-accounts',
        name: 'webhosting-accounts',
        component: WebhostingPage,
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'webhosting-accounts/:id/webhosting-account-details',
        name: 'webhosting-account-details',
        component: WebhostingDetailsPage,
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'webhosting-accounts/new-webhosting-account',
        name: 'new-webhosting-account',
        component: NewWebhostingPage,
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'domains',
        name: 'domains',
        component: DomainsPage,
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'domains/:id/domain-details',
        name: 'domain-details',
        component: DomainDetailsPage,
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'domains/new-domain',
        name: 'new-domain',
        component: NewDomainPage,
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'webhooks',
        component: () => import('pages/customer/WebhooksPage.vue'),
        meta: { requiresAuth: true },
        children: [
          { path: '', name: 'webhooks', redirect: { name: 'webhooks-configurations' } },
          {
            path: 'configurations',
            name: 'webhooks-configurations',
            component: () => import('pages/customer/Webhooks/CustomerWebhooksOverview.vue'),
            props: { itemsService: 'webhooks' },
            meta: { requiresAuth: true, webhookService: 'webhooks' },
          },
          {
            path: 'deliveries',
            name: 'webhooks-deliveries',
            component: () => import('pages/customer/Webhooks/CustomerWebhooksOverview.vue'),
            props: { itemsService: 'webhook-queue' },
            meta: { requiresAuth: true, webhookService: 'webhook-queue' },
          },
          {
            path: 'logs',
            name: 'webhooks-logs',
            component: () => import('pages/customer/Webhooks/CustomerWebhooksOverview.vue'),
            props: { itemsService: 'webhook-logs' },
            meta: { requiresAuth: true, webhookService: 'webhook-logs' },
          },
        ],
      },
      {
        path: '',
        component: StatisticsPage,
        meta: { requiresAuth: true },
        children: [
          { path: 'statistics', name: 'statistics', redirect: { name: 'statistics-overview' } },
          {
            path: 'statistics/overview',
            name: 'statistics-overview',
            component: () => import('pages/customer/SharingCenter/StatisticsOverviewPage.vue'),
            meta: { requiresAuth: true, label: 'Overview' },
          },
          {
            path: 'statistics/meetings',
            name: 'statistics-meetings',
            component: () => import('pages/customer/statistics/SharingCenterMeetingStatisticsPage.vue'),
            meta: { requiresAuth: true, label: 'Meetings' },
          },
          {
            path: 'statistics/meetings/:appointmentId',
            name: 'statistics-meetings-detail',
            component: () => import('pages/customer/statistics/SharingCenterMeetingStatisticsDetailPage.vue'),
            meta: { requiresAuth: true, label: 'Meeting details' },
            props: true,
          },
          {
            path: 'statistics/feedback',
            name: 'statistics-feedback',
            component: () => import('pages/customer/statistics/FeedbackStatisticsPage.vue'),
            meta: { requiresAuth: true, label: 'Feedback statistics' },
          },
          {
            path: 'statistics/sms',
            name: 'statistics-sms',
            component: () => import('pages/customer/statistics/SmsStatisticsPage.vue'),
            meta: { requiresAuth: true, label: 'SMS statistics' },
          },
          {
            path: 'statistics/email',
            name: 'statistics-email',
            component: () => import('pages/customer/statistics/EmailStatisticsPage.vue'),
            meta: { requiresAuth: true, label: 'Email statistics' },
          },
          {
            path: 'statistics/licenses',
            name: 'statistics-licenses',
            component: () => import('pages/customer/statistics/LicenseStatisticsPage.vue'),
            meta: { requiresAuth: true, label: 'License statistics' },
          },
        ],
      },
      {
        path: '',
        component: SharingCenterPage,
        meta: { requiresAuth: true, requiredLicense: 'OMNI-SESSIONS' },
        children: [
          { path: 'omni-sessions', alias: 'sharing-center', name: 'sharing-center', redirect: { name: 'sc-overview' } },
          {
            path: 'omni-sessions/overview',
            alias: 'sharing-center/overview',
            name: 'sc-overview',
            component: () => import('pages/customer/SharingCenter/SharingCenterOverview.vue'),
            meta: { requiresAuth: true, label: 'Overview' },
          },
          {
            path: 'omni-sessions/new-appointment',
            alias: 'sharing-center/new-appointment',
            name: 'sc-new-appointment',
            component: () => import('pages/customer/SharingCenter/SharingCenterNewAppointment.vue'),
            meta: { requiresAuth: true, label: 'New appointment' },
          },
          {
            path: 'omni-sessions/appointment-form/:id?',
            alias: 'sharing-center/appointment-form/:id?',
            name: 'sc-appointment-form',
            component: () => import('pages/customer/SharingCenter/SharingCenterAppointmentForm.vue'),
            meta: { requiresAuth: true, label: 'New appointment' },
            props: true,
          },
          {
            path: 'omni-sessions/calendar',
            alias: 'sharing-center/calendar',
            name: 'sc-calendar',
            component: () => import('pages/customer/SharingCenter/SharingCenterCalendars.vue'),
            meta: { requiresAuth: true, label: 'Calendar' },
          },
          {
            path: 'omni-sessions/recordings',
            alias: 'sharing-center/recordings',
            name: 'sc-meeting-recordings',
            component: () => import('pages/customer/SharingCenter/SharingCenterMeetingRecordingsPage.vue'),
            meta: { requiresAuth: true, label: 'Recordings' },
          },
          {
            path: 'omni-sessions/meeting-statistics',
            alias: 'sharing-center/meeting-statistics',
            name: 'sc-meeting-statistics',
            redirect: (to) => ({ name: 'statistics-meetings', query: to.query, hash: to.hash }),
          },
          {
            path: 'omni-sessions/meeting-statistics/:appointmentId',
            alias: 'sharing-center/meeting-statistics/:appointmentId',
            name: 'sc-meeting-statistics-detail',
            redirect: (to) => ({ name: 'statistics-meetings-detail', params: to.params, query: to.query, hash: to.hash }),
          },
          {
            path: 'omni-sessions/call-colleague',
            alias: 'sharing-center/call-colleague',
            name: 'sc-call-colleague',
            component: () => import('pages/customer/SharingCenter/SharingCenterColleaguesPage.vue'),
            meta: { requiresAuth: true, label: 'Call colleague' },
          },
          {
            path: 'omni-sessions/contacts',
            alias: 'sharing-center/contacts',
            name: 'sc-contacts',
            component: () => import('pages/customer/SharingCenter/SharingCenterContacts.vue'),
            meta: { requiresAuth: true, label: 'Contacts' },
          },
          {
            path: 'omni-sessions/profiles',
            alias: 'sharing-center/profiles',
            name: 'sc-profiles',
            component: () => import('pages/customer/SharingCenter/SharingCenterProfiles.vue'),
            meta: { requiresAuth: true, label: 'Profiles' },
          },
          {
            path: 'omni-sessions/groups',
            alias: 'sharing-center/groups',
            name: 'sc-groups',
            component: () => import('pages/customer/SharingCenter/SharingCenterGroups.vue'),
            meta: { requiresAuth: true, label: 'Groups' },
          },
          {
            path: 'omni-sessions/settings',
            alias: 'sharing-center/settings',
            name: 'sc-settings',
            component: UserSettingsPage,
            meta: { requiresAuth: true, label: 'Settings' },
          },
          {
            path: 'omni-sessions/profile',
            alias: 'sharing-center/profile',
            name: 'sc-profile',
            component: ProfilePage,
            meta: { requiresAuth: true, label: 'Profile' },
          },
          {
            path: 'meeting/:token',
            name: 'sc-meeting',
            component: () => import('pages/customer/SharingCenter/meeting/MeetingPage.vue'),
            meta: { requiresAuth: false, requiresMFA: false, label: 'Meeting' },
            props: true,
          },
        ],
      },
    ],
  },
  {
    path: '/omni-sessions/rsvp',
    alias: '/sharing-center/rsvp',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'sharing-center-rsvp',
        component: SharingCenterRsvpPage,
        meta: {
          requiresAuth: false,
          requiresMFA: false,
        },
      },
    ],
  },
  {
    path: '/mail/unsubscribe',
    name: 'unsubscribe-mail',
    component: UnsubscribeMailPage,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/docs',
    alias: ['/docs/'],
    component: DocsPassthroughPage,
    meta: {
      requiresAuth: false,
      requiresMFA: false,
    },
  },
  {
    path: '/docs/admin/:pathMatch(.*)*',
    component: DocsPassthroughPage,
    meta: {
      requiresAuth: false,
      requiresMFA: false,
    },
  },
  {
    path: '/docs/customer/:pathMatch(.*)*',
    component: DocsPassthroughPage,
    meta: {
      requiresAuth: false,
      requiresMFA: false,
    },
  },
  {
    path: '/docs/authentication/:pathMatch(.*)*',
    component: DocsPassthroughPage,
    meta: {
      requiresAuth: false,
      requiresMFA: false,
    },
  },
  // Always leave this as last one,
  // but you can also remove it
  {
    path: '/:catchAll(.*)*',
    component: ErrorNotFound,
    meta: {
      requiresAuth: false,
    },
  },
]

export default routesCustomerPortal
